import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { useRegenerateLocalityMap } from 'src/api/positions'
import { z } from 'zod'
import { IMoveReportTarget } from 'src/generated-types/form-schema'
import { invalidateDeviation, useMoveDeviationEquipment, useUpdateDeviation } from '../../api/deviation'
import { invalidateMainComponentCache } from '../../api/main-component'
import { DeviationStepperForm } from '../../forms/DeviationStepperForm/DeviationStepperForm'
import { IDeviation } from '../../generated-types/deviation'
import { IDeviationEventSupervision } from '../../generated-types/deviation-event-supervision'
import { IDeviationMeasure } from '../../generated-types/deviation-measure'
import { IDeviationPriority } from '../../generated-types/deviation-priority'
import { IDeviationType } from '../../generated-types/deviation-type'
import { IFormType } from '../../generated-types/form-type'
import { EnumToSelectTypes } from '../../generated-types/select-type'
import { useZodForm } from '../../hooks/zod-form'
import { ShowDialogProps } from '../../show-dialog'
import { canMoveDeviation, moveTargetFor, saveDeviationFailureKey } from './UpdateDeviationDialog.action'
import { UpdateDeviationEquipmentSection } from './UpdateDeviationEquipmentSection'

interface UpdateDeviationDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId?: EntityId
  componentId?: EntityId
  deviation: IDeviation
  selectedLocalityId?: EntityId
  permission?: number
}

const UpdateDeviationDialog: React.FC<UpdateDeviationDialogProps> = ({
  pop,
  customerId,
  localityId,
  componentId,
  deviation,
  selectedLocalityId,
  permission,
}) => {
  const { t } = useTranslation(['deviation', 'common'])
  const updateMutation = useUpdateDeviation(customerId, deviation.id)
  const moveMutation = useMoveDeviationEquipment(customerId, deviation.id)
  const [selectedEquipment, setSelectedEquipment] = useState<IMoveReportTarget>()
  const { mutateAsync: regenerateMap } = useRegenerateLocalityMap(
    customerId as EntityId,
    selectedLocalityId as EntityId,
  )

  const moveToEquipment = async (mainComponentId: number) => {
    await moveMutation.mutateAsync({ mainComponentId })
    invalidateDeviation.afterMove({
      customerId,
      deviationId: deviation.id,
      fromEquipmentId: deviation.mainComponentId as number,
      toEquipmentId: mainComponentId,
    })
  }

  const onUpdated = () => {
    invalidateDeviation.invalidateAllDeviations(customerId, undefined, undefined, deviation?.id)
    invalidateMainComponentCache.getMainComponentById(customerId, componentId as EntityId)
    invalidateDeviation.getDeviationDocuments(customerId, componentId as EntityId)
    invalidationActivityLogCache.getCageActivityLogs()
    invalidateDeviation.getDeviations(customerId)
    toast.success(t('updated-deviation-successfully-toast', { ns: 'deviation' }))
    pop()
    regenerateMap({
      onSuccess: () => {},
      onError: () => {},
    })
  }

  const onSubmit = async form => {
    const target = moveTargetFor(deviation, selectedEquipment ? Number(selectedEquipment.id) : null)
    const failure = await saveDeviationFailureKey(
      { move: moveToEquipment, update: values => updateMutation.mutateAsync(values) },
      target,
      form,
    )
    if (failure) return toast.error(t(failure, { ns: 'common-api-message' }))
    onUpdated()
  }

  const updateDeviationSchema = z.object({
    title: z.string(),
    description: z.string(),
    keywords: z
      .any({
        errorMap: () => {
          return { message: t('selectOneOfTheGivenOptions', { ns: 'common' }) }
        },
      })
      .optional(),
    visualDeviation: z.boolean().optional(),
    measuredDeviation: z.boolean().optional(),
    normalWear: z.boolean().optional(),
    abnormalWear: z.boolean().optional(),
    measure: z
      .object({
        id: z.number(),
      })
      .transform(value => value.id),
    eventSupervision: z
      .object({
        id: z.number(),
      })
      .transform(value => value.id),
    reason: z.string(),
    measures: z.string(),
    fileIds: z.any(),
  })

  const deviationPriorities = EnumToSelectTypes(IDeviationPriority, 'deviation')
  const deviationTypes = EnumToSelectTypes(IDeviationType, 'deviation')
  const deviationMeasures = EnumToSelectTypes(IDeviationMeasure, 'deviation')
  const deviationEventSupervision = EnumToSelectTypes(IDeviationEventSupervision, 'deviation')

  const { priority, dueDate, type, measure, eventSupervision, keywords, ...rest } = deviation

  const form = useZodForm(updateDeviationSchema, {
    defaultValues: {
      priority: deviationPriorities[priority],
      type: deviationTypes[type],
      measure: deviationMeasures[measure],
      eventSupervision: deviationEventSupervision[eventSupervision],
      dueDate: moment(dueDate).format('YYYY-MM-DD'),
      keywords: deviation.keywordsArray,
      ...rest,
    },
  })

  return (
    <DeviationStepperForm
      onSubmit={onSubmit}
      onCancel={() => pop()}
      customerId={customerId}
      localityId={localityId}
      isDisableWizard={true}
      type={deviation.type}
      formType={IFormType.Update}
      form={form}
      title={`${t('updateDeviation')} - ${deviation.title}`}
      isLoading={updateMutation.isLoading || moveMutation.isLoading}
      formHeader={
        canMoveDeviation(permission, deviation) && (
          <UpdateDeviationEquipmentSection
            customerId={customerId}
            localityId={localityId}
            mainComponentId={deviation.mainComponentId as number}
            selected={selectedEquipment}
            onSelect={setSelectedEquipment}
          />
        )
      }
    />
  )
}

export default UpdateDeviationDialog
